import { useState, useCallback } from 'react';
import { SocialMediaItem } from '../types';
import { SOCIAL_MEDIA_OPTIONS } from '../constants';

export const useSocialMedia = () => {
  const [editingSocialMedia, setEditingSocialMedia] = useState<SocialMediaItem[]>([]);
  const [showingSocialMedia, setShowingSocialMedia] = useState(false);

  const startEditing = useCallback((currentSocialMedia: SocialMediaItem[]): void => {
    const socialMediaWithIds = Array.isArray(currentSocialMedia) && currentSocialMedia.length > 0 
      ? currentSocialMedia.map((item, index) => ({ 
          ...item, 
          id: item.id || `social-${Date.now()}-${index}` 
        }))
      : [];
    setEditingSocialMedia(socialMediaWithIds);
  }, []);

  const addSocialMedia = useCallback((): void => {
    const usedTypes = editingSocialMedia.map((item: SocialMediaItem) => item.type);
    const availableType = SOCIAL_MEDIA_OPTIONS.find(option => !usedTypes.includes(option.value));
    
    if (availableType) {
      const newItem: SocialMediaItem = {
        id: `social-${Date.now()}-${editingSocialMedia.length}`,
        type: availableType.value as 'facebook' | 'instagram' | 'linkedin' | 'google',
        url: ''
      };
      setEditingSocialMedia([...editingSocialMedia, newItem]);
    }
  }, [editingSocialMedia]);

  const updateSocialMediaType = useCallback((id: string, type: string): void => {
    if (!type || type.trim() === '' || type === null) {
      return;
    }
    
    const currentSocialMedia = [...editingSocialMedia];
    const itemIndex = currentSocialMedia.findIndex(item => item.id === id);
    
    if (itemIndex === -1) return;
    
    const isTypeUsed = currentSocialMedia.some((item: SocialMediaItem) => 
      item.id !== id && item.type === type
    );
    
    if (!isTypeUsed) {
      currentSocialMedia[itemIndex] = { 
        ...currentSocialMedia[itemIndex], 
        type: type as 'facebook' | 'instagram' | 'linkedin' | 'google' 
      };
      setEditingSocialMedia(currentSocialMedia);
    }
  }, [editingSocialMedia]);

  const updateSocialMediaUrl = useCallback((id: string, url: string): void => {
    const currentSocialMedia = [...editingSocialMedia];
    const itemIndex = currentSocialMedia.findIndex(item => item.id === id);
    
    if (itemIndex !== -1) {
      currentSocialMedia[itemIndex] = { ...currentSocialMedia[itemIndex], url };
      setEditingSocialMedia(currentSocialMedia);
    }
  }, [editingSocialMedia]);

  const removeSocialMedia = useCallback((id: string): void => {
    const currentSocialMedia = editingSocialMedia.filter(
      (item: SocialMediaItem) => item.id !== id
    );
    setEditingSocialMedia(currentSocialMedia);
  }, [editingSocialMedia]);

  const toggleDisplay = useCallback((): void => {
    setShowingSocialMedia(prev => !prev);
  }, []);

  const resetEditing = useCallback((): void => {
    setEditingSocialMedia([]);
  }, []);

  return {
    editingSocialMedia,
    showingSocialMedia,
    startEditing,
    addSocialMedia,
    updateSocialMediaType,
    updateSocialMediaUrl,
    removeSocialMedia,
    toggleDisplay,
    resetEditing
  };
};

